import type { Store } from 'store/types';

import styled from '@emotion/styled';
import { Button } from '@kizen/kds/Button';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { fontWeights } from 'app/typography';
import InputControl from '__components/Inputs/InputControl';
import Icon from '__components/Kizen/Icon';
import { TextEllipsisWithTooltip } from '__components/Kizen/Table';
import { Fullscreen } from 'components/Modals/Fullscreen';
import ConfirmNavigationWithActionModal from '__components/Modals/presets/ConfirmNavigationWithAction';
import MultiColumnBuilder from 'components/MultiColumnBuilder';
import BaseOption from 'components/MultiColumnBuilder/components/BaseOption';
import useBasicBuilder, {
  LEFT_ID,
  RIGHT_ID,
} from '__components/MultiColumnBuilder/useBasicBuilder';
import { ColumnsHeaderPreview } from 'components/Wizards/shared/components/TableBuilder/components/ColumnsHeaderPreview';
import { customObjectFieldsQuery } from 'query/custom-object';

type TeamMemberColumnsModalProps = {
  columns: string[];
  onHide(): void;
  onConfirm(columns: Array<{ name: string }>): void;
};

const MultiColumnBuilderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 140px);
  padding: 20px;
  background-color: var(--color-background-white);
`;

const EMPTY_ARRAY: any[] = [];
const selfDisabledDropZones = [LEFT_ID];

const getDefaultFields = (t: (x: string) => string) => {
  return [
    {
      display_name: t('Additional Permission Groups'),
      name: 'permission_groups',
    },
    { display_name: t('Annual Sales Goal'), name: 'annual_sales_goal' },
    { display_name: t('Email'), name: 'email' },
    { display_name: t('Last Login'), name: 'last_login' },
    { display_name: t('Phone Number'), name: 'phone' },
    { display_name: t('Roles'), name: 'roles' },
    { display_name: t('Start Date'), name: 'start_date' },
    { display_name: t('Status'), name: 'verified' },
  ].map((field) => {
    return { ...field, id: field.name };
  });
};

export const TeamMemberColumnsModal = (props: TeamMemberColumnsModalProps) => {
  const { columns, onConfirm, onHide } = props;

  const { t } = useTranslation();
  const objectId = useSelector((state: Store) => {
    return state.authentication.chosenBusiness?.employee_object.id;
  });

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const fieldsQuery = useQuery(
    customObjectFieldsQuery(objectId, {
      enabled: Boolean(objectId),
      select: (data) => {
        const fields = data
          .map((field) => {
            const { display_name, id, name } = field;

            return { display_name, id, name };
          })
          .sort((a, b) => {
            return a.display_name < b.display_name ? -1 : 1;
          });

        const result = getDefaultFields(t)
          .concat(fields)
          .map((field) => {
            return { ...field, label: field.display_name };
          });

        const ordering = result.map((field) => field.id);

        return { fields: result, ordering };
      },
    })
  );

  const { fields = EMPTY_ARRAY, ordering = EMPTY_ARRAY } =
    fieldsQuery.data ?? {};

  const initialItems = useMemo(() => {
    const left = [];
    const right = [
      {
        id: 'full_name',
        display_name: t('Team Member'),
        label: t('Team Member'),
        name: 'full_name',
        isLocked: true,
      },
    ];

    for (const field of fields) {
      const index = columns.indexOf(field.name);

      if (index >= 0) {
        right[index] = { ...field, isLocked: false };
      } else {
        left.push(field);
      }
    }

    return { left, right };
  }, [fields, columns, t]);

  const additionalActions = useCallback(
    ({ swapItemColumn, index, element, section }: any) => {
      const showDelete = section === RIGHT_ID && element.id !== 'full_name';

      if (
        !element ||
        element.disabled ||
        element.restricted ||
        !showDelete ||
        !swapItemColumn
      ) {
        return null;
      }

      return (
        <div>
          <Icon
            className="DeleteAction"
            icon="delete"
            onClick={() => {
              swapItemColumn(index, section);
            }}
          />
        </div>
      );
    },
    []
  );

  const builder = useBasicBuilder({
    editable: true,
    initialLeftColumnItems: initialItems.left,
    initialRightColumnItems: initialItems.right,
    showCategoryOnSearch: false,
    itemsOrdering: ordering,
    selfDisabledDropZones,
    rightColumnItemsWithContentForceDirty: false,
  });

  const { setOptionsSearchTerm, rightColumnItems, ...builderProps } = builder;
  const { dirty } = builderProps;

  return (
    <Fullscreen.Modal>
      <Fullscreen.Header>
        <Fullscreen.HeaderContent
          backButtonText={t('back to team members')}
          title={t('Edit Team Member Columns')}
          onGoBack={() => {
            if (dirty) {
              setShowConfirmationModal(true);
            } else {
              onHide();
            }
          }}
        >
          <Button
            onClick={() => {
              onConfirm(rightColumnItems);
              onHide();
            }}
          >
            {t('Save')}
          </Button>
        </Fullscreen.HeaderContent>
      </Fullscreen.Header>
      <Fullscreen.Body>
        <MultiColumnBuilderWrapper>
          <InputControl label={t('Column Preview')}>
            <ColumnsHeaderPreview columns={rightColumnItems} />
          </InputControl>
          <MultiColumnBuilder
            {...builderProps}
            weight={fontWeights.regular}
            leftHeaderText={t('Available Columns (Drag to Add)')}
            rightHeaderText={t('Active Table Columns')}
            searchPlaceholder={t('Find Options')}
            onChangeSearchTerm={setOptionsSearchTerm}
            disabledDropZoneText={false}
            rollBackDrop={false}
            renderRightEmpty={{
              onDropLabel: t('Place Here'),
              noItems: t(
                'No Columns Currently Selected (Drag and Drop to Add)'
              ),
            }}
            renderLeftEmpty={{
              onDropLabel: t('Drop to Remove'),
              noItems: t('No Options Found'),
            }}
            renderOption={(props: any) => {
              return (
                <BaseOption additionalActions={additionalActions} {...props}>
                  {({ element }: any) => {
                    return (
                      <TextEllipsisWithTooltip>
                        {element.display_name}
                      </TextEllipsisWithTooltip>
                    );
                  }}
                </BaseOption>
              );
            }}
          />
        </MultiColumnBuilderWrapper>
      </Fullscreen.Body>
      <ConfirmNavigationWithActionModal
        when={dirty}
        show={showConfirmationModal}
        heading={t('Your Columns Have Unsaved Changes')}
        buttonText={t('Save')}
        actionBtnColor="green"
        additionalButtonText={t('Discard Changes')}
        additionalButtonColor="red"
        action={() => onConfirm(rightColumnItems)}
        onHide={() => setShowConfirmationModal(false)}
        onConfirm={() => {
          setShowConfirmationModal(false);
          onHide();
        }}
      >
        {t('All unsaved changes will be lost unless you save your columns.')}
      </ConfirmNavigationWithActionModal>
    </Fullscreen.Modal>
  );
};
